import { Grid } from '@mui/material';
import { RenderSanityBlock } from '@/components/RenderSanityBlock';
import { BlogCategories } from '@/common/constants/blog';
import { ContentType } from '@/common/constants/ContentTypes';
import CustomAvatar from '@/components/CustomAvatar';
import {
  DesktopAvatarWrapper,
  MobileAvatarWrapper,
} from '@/components/PostBase/PostBase.styles';
import { Chip } from '@/components/Chip/Chip';
import { useMemo, useRef } from 'react';
import ArticleSourceLink from '@/domains/blogs/pages/BlogPostPage/components/ArticleSourceLink/ArticleSourceLink';
import { Content, ExternalLinkWrapper } from './styles';
import { BlogContentType } from '../../types';
import { AuthorAndDateTime } from '../../pages/BlogPostPage/components/AuthorAndTime';

export function ContentSection({
  content,
  contentType,
}: {
  content: BlogContentType;
  contentType: ContentType;
}): JSX.Element {
  const {
    body,
    resourceType,
    author,
    createdAt,
    updatedAt,
    externalSource,
  } = content;
  const { type } = resourceType;
  const contentRef = useRef(null);

  const shouldCollapse = useMemo(() => {
    const isProductWithContent =
      contentType === ContentType.PRODUCTS && content.body != null;
    const isArticleWithTLDR =
      contentType === ContentType.RESOURCES &&
      type === BlogCategories.ARTICLES &&
      content.tldr != null;

    return isProductWithContent || isArticleWithTLDR;
  }, [content.body, content.tldr, contentType, type]);

  return (
    <>
      <Grid
        container
        gridArea="content"
        display="grid"
        justifyContent="center"
        gridTemplateColumns={(theme) => ({
          xs: '1fr',
        })}
      >
        {!shouldCollapse && !!author && (
          <DesktopAvatarWrapper
            container
            item
            flexDirection="column"
            alignItems="center"
            gap={4}
          >
            <CustomAvatar
              name={author?.firstName}
              image={author?.image?.url}
              size={{ desktop: 20, mobile: 5 }}
              fontSize={{ desktop: 12, mobile: 10 }}
            />
            <Chip
              variant="highlighted"
              label="Author"
              size="small"
              color="primary"
            />
          </DesktopAvatarWrapper>
        )}

        <Grid item container gridColumn={{ xs: 'auto', sm: '2/3' }} gap={3}>
          {!shouldCollapse && !!author && (
            <Grid item container paddingX={{ xs: 7, sm: 8 }} gap={3}>
              <MobileAvatarWrapper
                flexDirection="column"
                gap={2}
                alignItems="center"
              >
                <CustomAvatar
                  name={author?.firstName}
                  image={author?.image?.url}
                  size={{ desktop: 20, mobile: 10 }}
                  fontSize={{ desktop: 12, mobile: 10 }}
                />
                <Chip
                  variant="highlighted"
                  label="Author"
                  size="small"
                  color="primary"
                />
              </MobileAvatarWrapper>
              <AuthorAndDateTime
                author={author}
                dateTime={updatedAt || createdAt}
              />
            </Grid>
          )}
          <Content
            id="articleContent"
            ref={contentRef}
            upSMPaddingLeft={author ? 8 : 0}
          >
            <RenderSanityBlock blocks={body} />
          </Content>
        </Grid>
      </Grid>
      {!!externalSource?.url && (
        <ExternalLinkWrapper>
          <ArticleSourceLink
            sourceUrl={externalSource.url}
            text={externalSource.text || externalSource.provider.name}
            icon={externalSource.provider.icon.url}
          />
        </ExternalLinkWrapper>
      )}
    </>
  );
}
